import React, { useState } from 'react';
import {
    Box,
    Flex,
    HStack,
    NumberDecrementStepper,
    NumberIncrementStepper,
    NumberInput,
    NumberInputField,
    NumberInputStepper,
    Slider,
    SliderFilledTrack,
    SliderThumb,
    SliderTrack,
    Text,
    useColorModeValue,
    VStack,
} from '@chakra-ui/react';
import { useIntl } from 'react-intl';

const TIERS = [
    { upTo: 10, price: 1.4 },
    { upTo: 20, price: 1.3 },
    { upTo: Infinity, price: 1.1 },
];

function calculatePrice(userCount: number): {
    total: number;
    breakdown: { tier: string; users: number; price: number; subtotal: number }[];
} {
    const breakdown: { tier: string; users: number; price: number; subtotal: number }[] = [];
    let remaining = userCount;
    let prev = 0;

    for (const tier of TIERS) {
        if (remaining <= 0) break;
        const tierCapacity = tier.upTo === Infinity ? remaining : tier.upTo - prev;
        const usersInTier = Math.min(remaining, tierCapacity);
        const label = tier.upTo === Infinity ? `${prev + 1}+` : `${prev + 1}-${tier.upTo}`;
        breakdown.push({
            tier: label,
            users: usersInTier,
            price: tier.price,
            subtotal: usersInTier * tier.price,
        });
        remaining -= usersInTier;
        prev = tier.upTo;
    }

    const total = breakdown.reduce((sum, b) => sum + b.subtotal, 0);
    return { total, breakdown };
}

export const PricingCalculator: React.FC = () => {
    const intl = useIntl();
    const [userCount, setUserCount] = useState(10);
    const bg = useColorModeValue('white', 'gray.700');

    const { total, breakdown } = calculatePrice(userCount);

    const handleSliderChange = (val: number) => setUserCount(val);
    const handleInputChange = (_: string, val: number) => {
        if (isNaN(val) || val < 1) {
            setUserCount(1);
        } else if (val > 200) {
            setUserCount(200);
        } else {
            setUserCount(val);
        }
    };

    return (
        <Box bg={bg} borderRadius={'xl'} boxShadow={'md'} p={{ base: 5, md: 8 }} mt={6} mx={2}>
            <Text fontWeight={700} fontSize={'xl'} textAlign={'center'} mb={6}>
                {intl.formatMessage({ id: 'pricing.calculator.title' })}
            </Text>

            <VStack spacing={6} align="stretch">
                <Box>
                    <Text fontWeight={600} mb={2} textColor="gray.400">
                        {intl.formatMessage({ id: 'pricing.calculator.enterUsers' })}
                    </Text>
                    <HStack spacing={4}>
                        <Slider
                            flex={1}
                            min={1}
                            max={200}
                            value={userCount > 200 ? 200 : userCount}
                            onChange={handleSliderChange}
                            focusThumbOnChange={false}
                        >
                            <SliderTrack bg="gray.200">
                                <SliderFilledTrack bg="primary.300" />
                            </SliderTrack>
                            <SliderThumb boxSize={6} bg="primary.300" />
                        </Slider>
                        <NumberInput min={1} max={200} value={userCount} onChange={handleInputChange} maxW={'100px'}>
                            <NumberInputField fontWeight={600} />
                            <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                            </NumberInputStepper>
                        </NumberInput>
                    </HStack>
                </Box>

                <VStack spacing={2} align="stretch">
                    {breakdown.map((b) => (
                        <Flex key={b.tier} justify="space-between" px={2}>
                            <Text textColor="gray.400" fontSize={'sm'}>
                                {b.users}x {intl.formatMessage({ id: 'pricing.user' })} ({b.tier}) &times; {b.price.toFixed(2)}€
                            </Text>
                            <Text textColor="gray.400" fontSize={'sm'} fontWeight={500}>
                                {b.subtotal.toFixed(2)}€
                            </Text>
                        </Flex>
                    ))}
                </VStack>

                <Flex justify="space-between" align="center" bg="primary.300" borderRadius={'lg'} px={4} py={3}>
                    <Text fontWeight={700} fontSize={'lg'} color="gray.800">
                        {intl.formatMessage({ id: 'pricing.calculator.monthlyTotal' })}
                    </Text>
                    <Text fontWeight={700} fontSize={'2xl'} color="gray.800">
                        {total.toFixed(2)}€ / {intl.formatMessage({ id: 'pricing.calculator.month' })}
                    </Text>
                </Flex>
            </VStack>
        </Box>
    );
};
